import NakedPromise from 'src/game/minigame/match2-timestep/lib/pattern/NakedPromise';
import { LineLogin } from '@play-co/native-game-js/modules/LineLogin';
import { LaunchScreen } from '@play-co/native-game-js/modules/LaunchScreen';
import { captureGenericError } from '../sentry';
import { GCInstant } from '../gcinstant';
import {
  attemptLineLogin,
  checkLineAuthentication,
} from './native.line.authentication';
import { nativeLoginUI } from './native.login.ui';
import { waitForItPromise } from '../utils';
import { duration } from '@play-co/components';
import { config } from 'src/replicant/config';
import { hasWebPlayerCredentials } from '@play-co/replicant';
import { tryLocalStorage } from '../tryLocalStorage';
import { forceGameUpdateAndReentry } from './native.util';
import StateObserver from 'src/StateObserver';
import { hideLoading, showLoading } from 'src/state/ui';

/** We use it to request login screen */
const REQUIRE_LOGIN_PARAM = 'requireLogin';
const GUEST_MODE_LOGGED_IN = 'guestModeLoggedIn';

/**
 * Represents the possible outcomes of a native login operation.
 *
 * - `'default'`: The default or unspecified login result.
 * - `'line_authorized'`: The user has authorized via LINE.
 * - `'line_login'`: The user has logged in using LINE.
 * - `'apple_login'`: The user has logged in using Apple.
 * - `'guest_login'`: The user has logged in as a guest.
 */
export type NativeLoginResult =
  | 'default'
  | 'line_authorized'
  | 'line_login'
  | 'apple_login'
  | 'guest_login';

/**
 * Determines whether the native login option can be displayed.
 *
 * This function checks if the current environment is the iOS app and if the
 * `LineLogin` module is available (which is only exposed in build 232+ of the iOS native app).
 * Returns `true` if both conditions are met, indicating that the native login can be shown.
 *
 * @returns {boolean} `true` if native login can be displayed; otherwise, `false`.
 */
export function canDisplayNativeLogin() {
  // If the user is not logged in, we can display the native login.
  // Should only be displayed on iOS app
  if (GCInstant.osType !== 'IOS_APP') return false;
  // Native login can only work if the LineLogin module is exposed
  // This module is only enabled in build 232+ of the iOS native app.
  return !!LineLogin.getInstance();
}

/**
 * Displays the native login UI and handles user authentication flow.
 *
 * This function checks if the native login can be displayed, verifies if the user is already authenticated,
 * and if not, presents a login UI with options for LINE login, Apple login, or guest login.
 * Resolves with the result of the login process.
 *
 * @returns {Promise<NativeLoginResult>} A promise that resolves to the result of the login attempt:
 *   - `'default'` if native login cannot be displayed,
 *   - `'line_authorized'` if the user is already authenticated or successfully logs in with LINE,
 *   - `'apple_login'` if the user selects Apple login,
 *   - `'guest_login'` if the user selects guest login.
 */
export async function showNativeLogin(
  debug: Record<string, unknown>,
): Promise<NativeLoginResult> {
  debug.nativeLoginUIShown = false;
  debug.canDisplayNativeLogin = canDisplayNativeLogin();

  // Bail out if user can't use the native login.
  if (!canDisplayNativeLogin()) {
    return 'default';
  }

  debug.checkLineAuthenticationFailed = false;

  // Check if already authenticated, and skip login if so.
  const authenticated = await checkLineAuthentication().catch((e) => {
    debug.checkLineAuthenticationFailed = true;
    debug.checkLineAuthenticationErrorMessage = e.message;
    captureError('Error checking LINE authentication:', e);
    tryLocalStorage()?.removeItem(GUEST_MODE_LOGGED_IN);
    return false;
  });

  debug.userIsAlreadyAuthenticated = authenticated;

  // Line auto login
  // If the user is already authenticated in line, we can skip the login UI and load the game.
  if (authenticated) {
    LaunchScreen.getInstance()?.hide();
    tryLocalStorage()?.removeItem(GUEST_MODE_LOGGED_IN);
    tryLocalStorage()?.removeItem(REQUIRE_LOGIN_PARAM);
    return 'line_authorized';
  }

  debug.canAutoGuestLogin = false;

  // Auto login to guest mode.
  // If the user has web player credentials, is logged in as guest, and login is not required by URL.
  if (
    hasWebPlayerCredentials(config.appName) &&
    !isLoginRequired() &&
    isUserLoggedInAsGuest()
  ) {
    debug.canAutoGuestLogin = true;

    // If the user has web player credentials and doesn't require login, we can skip the login UI.
    LaunchScreen.getInstance()?.hide();
    return 'guest_login';
  }

  // Memo: New users auto guest mode login disabled by this requirement: https://github.com/play-co/buruburu/issues/9437

  // Create the login UI
  const { loginButton, guestButton, appleButton, loading, cleanup } =
    await nativeLoginUI();

  // Hide the launch screen.
  LaunchScreen.getInstance()?.hide();

  debug.nativeLoginUIShown = true;

  // Create the login promise that will resolve after done.
  const loginPromise = new NakedPromise<NativeLoginResult>();

  // Setup the login button.
  loginButton.onclick = async () => {
    loading(true);
    const success = await attemptLineLogin().catch((e) => {
      captureError('Error attempting LINE login:', e);
      loading(false);
      return false;
    });
    if (success) {
      loginPromise.resolve('line_authorized');
      cleanup();
    } else {
      loading(false);
    }
  };

  // Setup the guest mode buttons
  appleButton.onclick = async () => {
    loading(true);
    // Hide the launch screen.
    loginPromise.resolve('apple_login');
  };

  guestButton.onclick = async () => {
    loading(true);
    // Hide the launch screen.
    loginPromise.resolve('guest_login');
  };

  // Wait for login resolution.
  // This promise will resolve only after a successful login.
  const result = await loginPromise;

  // Remove the login UI after login resolution.
  cleanup();
  tryLocalStorage()?.removeItem(REQUIRE_LOGIN_PARAM);

  // If guest login, mark user as logged in guest mode
  if (result === 'guest_login' || result === 'apple_login') {
    tryLocalStorage()?.setItem(GUEST_MODE_LOGGED_IN, 'true');
  } else {
    tryLocalStorage()?.removeItem(GUEST_MODE_LOGGED_IN);
  }

  return result;
}

/**
 * Restarts the application by redirecting to the base game URL.
 * Optionally logs out the user from the native LINE login module before redirecting.
 *
 * @param requireLogin - If `true`, logs out the user from the native LINE login before restarting.
 * @returns A promise that resolves after waiting for the game to reload (up to 30 seconds).
 *
 * @remarks
 * - If the LINE login module is not available, the function exits early.
 * - When logging out, the URL is updated to require login on reload.
 * - The function waits for the game to reload after redirecting.
 */
export async function nativeRestart(requireLogin: boolean) {
  // Only if line login module is available
  const lineLogin = LineLogin.getInstance();

  // Legacy logout, just in case should never happen
  if (!lineLogin) {
    return;
  }

  // Check if the user is authenticated
  const authenticated = await checkLineAuthentication().catch((e) => {
    captureError('Error checking LINE authentication:', e);
    requireLogin = true; // Assume we need to logout if error occurs
    return true; // Assume authenticated if error occurs, maybe logout will help to fix it
  });

  // Restart without login or with login depending on requireLogin arg
  const flag = requireLogin ? 'true' : 'false';
  tryLocalStorage()?.setItem(REQUIRE_LOGIN_PARAM, flag);

  // Update the URL to require login on reload in case localstorage doesn't work
  const url = new URL(location.href);
  url.searchParams.set(REQUIRE_LOGIN_PARAM, flag);
  history.replaceState({}, '', url.toString());

  // We should logout if login required, but we need to check if the user is authenticated
  if (requireLogin && authenticated) {
    // Logout from native LINE using new native login module native iOS app 2.12.0+
    await lineLogin.logout();
  }

  // Reload app
  window.location.reload();

  // Wait for the game to reload
  return await waitForItPromise(duration({ seconds: 30 }));
}

/**
 * Attempts to log in to LINE using the native login flow.
 *
 * - If native login cannot be displayed, forces a game update and reentry.
 * - Shows a loading screen during the login attempt.
 * - Handles errors during the login attempt and captures them.
 * - Hides the loading screen after the attempt.
 * - If login is successful, proceeds with the game update and reentry.
 * - If login fails, falls back to the login screen.
 *
 * @returns {Promise<void>} Resolves when the login flow is complete.
 */
export async function loginToLINE() {
  // Legacy flow for LINE login
  if (!canDisplayNativeLogin()) {
    await forceGameUpdateAndReentry(true);
    return;
  }

  // Show loading screen while attempting LINE login
  StateObserver.dispatch(showLoading());

  // New native login flow
  const success = await attemptLineLogin()
    .catch((e) => {
      captureError('Failed to complete LINE login process:', e);
      return false;
    })
    .finally(() => {
      StateObserver.dispatch(hideLoading());
    });

  const shouldShowLoginScreen = !success;

  // If the login was successful, we can proceed with the game.
  // If it failed, we will show the login screen
  await forceGameUpdateAndReentry(shouldShowLoginScreen);
}

/**
 * Determines whether login is required based on URL query parameters or local storage.
 *
 * Checks if the `REQUIRE_LOGIN_PARAM` is set to `'true'` either in the current page's
 * URL search parameters or in local storage. If either condition is met, the function
 * returns `true`, indicating that the login UI should be shown.
 *
 * @returns {boolean} `true` if login is required, otherwise `false`.
 */
function isLoginRequired() {
  const url = new URL(location.href);

  const requireLoginURL = url.searchParams.get(REQUIRE_LOGIN_PARAM) === 'true';
  const requireLoginLocalStorage =
    tryLocalStorage()?.getItem(REQUIRE_LOGIN_PARAM) === 'true';

  // If the user has web player credentials and does not require login, we can skip the login UI.
  return requireLoginURL || requireLoginLocalStorage;
}

/**
 * Checks if the user is currently logged in as a guest.
 * We want to keep the user in guest mode across sessions.
 *
 * @returns {boolean} `true` if the user is logged in as a guest, otherwise `false`.
 */
function isUserLoggedInAsGuest() {
  return tryLocalStorage()?.getItem(GUEST_MODE_LOGGED_IN) === 'true';
}

/**
 * Wrapper for capturing errors, that will fallback to console.error if Sentry is not available.
 * @param message
 * @param error
 */
function captureError(message: string, error: unknown) {
  try {
    captureGenericError(message, error);
  } catch {
    console.error(message, error);
  }
}
